import { PaginatedContent } from '../paginatedContent';
import { useEffect, useMemo, useState, type ReactNode } from 'react';

export type PaginatedRequest = {
  pageNumber: number;
  pageSize: number;
};

export type PaginatedListMeta<T> = {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  isLoading: boolean;
};

export type PaginatedListProps<
  TItem,
  TParams extends object | null,
  TResponse,
> = {
  method: (params: TParams & PaginatedRequest) => Promise<TResponse>;
  params?: TParams;
  extract: (response: TResponse) => { items: TItem[]; totalCount?: number };
  initialPage?: number;
  pageSize?: number;
  className?: string;
  classNamePagination?: string;
  loadingFallback?: ReactNode;
  onMetaChange?: (meta: PaginatedListMeta<TItem>) => void;
  children: (items: TItem[]) => ReactNode;
};

export function PaginatedList<TItem, TParams extends object, TResponse>({
  method,
  params,
  extract,
  initialPage = 1,
  pageSize = 20,
  className = '',
  loadingFallback,
  onMetaChange,
  children,
  classNamePagination,
}: PaginatedListProps<TItem, TParams, TResponse>) {
  const [items, setItems] = useState<TItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(initialPage);
  const [isLoading, setIsLoading] = useState(false);

  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  useEffect(() => {
    setPageNumber(initialPage);
  }, [initialPage, paramsKey]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setIsLoading(true);
      try {
        const response = await method({
          ...(params ?? ({} as TParams)),
          pageNumber,
          pageSize,
        } as TParams & PaginatedRequest);
        if (cancelled) return;

        const data = extract(response);
        const nextItems = data.items ?? [];
        const nextTotalCount = data.totalCount ?? nextItems.length;

        setItems(nextItems);
        setTotalCount(nextTotalCount);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [method, extract, paramsKey, pageNumber, pageSize]);

  useEffect(() => {
    onMetaChange?.({
      items,
      totalCount,
      pageNumber,
      pageSize,
      isLoading,
    });
  }, [items, totalCount, pageNumber, pageSize, isLoading, onMetaChange]);

  if (isLoading && loadingFallback) {
    return <>{loadingFallback}</>;
  }

  return (
    <PaginatedContent
      pageNumber={pageNumber}
      pageSize={pageSize}
      totalCount={totalCount}
      onPageChange={setPageNumber}
      isLoading={isLoading}
      className={className}
      classNamePagination={classNamePagination}
    >
      {children(items)}
    </PaginatedContent>
  );
}
